import { useEffect, useMemo, useRef, useState } from "react";
import className from "classnames/bind";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Image, TypePill } from "@components";
import { DigitalExhibitHeroFragment } from "@graphqlTypes";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
import { MOBILE_MAX, TABLET_MAX } from "@/providers/ViewSizeContext";
import { getReduceMotion } from "@/utils/reduceMotion";
import { accentVar, getExhibitMarkup, selectValue } from "../utils";
import { useHeroScrubAudio } from "./useHeroScrubAudio";
import ArrowDownThin from "@/assets/svg/arrow-down-thin.svg";
import ArrowDownCta from "@/assets/svg/arrow-down-cta.svg";

import styles from "./DigitalExhibitHeroIntro.module.scss";
const cx = className.bind(styles);

gsap.registerPlugin(ScrollTrigger);

type HeroData = DigitalExhibitHeroFragment["digitalExhibitHero"];

type HeroIntroProps = {
  hero?: HeroData;
  statement?: string | null;
  // Intro accent select (spread from the intro group) — colors the statement's
  // pre-reveal highlighted words. Unique name (not "accent") so WPGraphQL-ACF
  // resolves the stored value rather than colliding with other groups' "accent".
  statementAccent?: string[] | null;
  knockoutEnabled?: boolean | null;
  knockoutColor?: string[] | null;
};

// Scroll choreography constants ported verbatim from the prototype (round5).
// Layout anchors mirror the prototype; sizes use the fluid rem root instead of
// the prototype's innerWidth math, so resize needs no re-init.
// See docs/digital-exhibit-motion-spec.md.
const VISUAL_SCROLL_PX = 800;
const TEXT_SCROLL_START = VISUAL_SCROLL_PX * 0.4;
const TEXT_SCROLL_PX_DESKTOP = 2400;
const TEXT_SCROLL_PX_MOBILE = 1800;
// Pin release (prototype nt): the stage unpins at t=0.75 of the text scroll on
// desktop, and after the statement entrance (t=0.35) + a 300px hold on mobile.
// The choreography tail (audio fade, prompt fade) finishes during the
// unpinned scroll-away — pinning the full text range reads as a dead hitch.
const PIN_SCROLL_DESKTOP = TEXT_SCROLL_START + TEXT_SCROLL_PX_DESKTOP * 0.75;
const PIN_SCROLL_MOBILE = TEXT_SCROLL_START + TEXT_SCROLL_PX_MOBILE * 0.35 + 300;
const WORD_REVEAL_START = 0.25;
const WORD_REVEAL_END = 0.6;
// Mobile renders the statement plain white (the prototype's mobileSimpleText):
// no accent highlight and no per-word scroll reveal. The reveal below is gated
// to non-mobile, and the accent color is scoped to bpMedium+ in the stylesheet.

const smoothstep = (v: number) => v * v * (3 - 2 * v);
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

export default function DigitalExhibitHeroIntro({ hero, statement, statementAccent }: HeroIntroProps) {
  const {
    backgroundDesktop,
    backgroundMobile,
    videoUrl,
    videoUrlMobile,
    videoPoster,
    logo,
    logoAltText,
    subheading,
    pillAccent,
    arrowOverride,
    arrowAccent,
  } = hero || {};
  const { labels } = usePageWrapperData();
  const [reduceMotion, setReduceMotionState] = useState(false);
  // Gates the entrance on the media being displayable: on slow connections
  // the border used to animate open around an empty media box, with the
  // poster blinking in mid-entrance. Until this flips, the __cover element
  // holds the whole stage black (SSR-rendered, so pre-hydration paints too)
  const [mediaReady, setMediaReady] = useState(false);

  const wrapperRef = useRef<HTMLElement>(null);
  const coverRef = useRef<HTMLDivElement>(null);
  const clipRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const frameDimRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const centerGroupRef = useRef<HTMLDivElement>(null);
  const lockupRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const logoBoxRef = useRef<HTMLDivElement>(null);
  const subheadRef = useRef<HTMLParagraphElement>(null);
  const arrowRef = useRef<HTMLDivElement>(null);
  const statementRef = useRef<HTMLParagraphElement>(null);
  const promptRef = useRef<HTMLDivElement>(null);

  // raw + smoothed progress (visual / text), mirroring the prototype's lerp loop
  const raw = useRef({ visual: 0, text: 0 });
  const smooth = useRef({ visual: 0, text: 0 });
  const rafId = useRef<number | null>(null);
  const revealedCount = useRef(-1);
  const entranceClip = useRef(0);
  // Lockup entrance scale (prototype: 0.88 -> 1 alongside the unblur). Lives
  // in a ref so applyFrame composes it with the scroll-driven lockup scale
  // instead of the tween and the frame loop fighting over style.transform.
  // 1 = settled, so the reduced-motion path renders the lockup at rest.
  const entranceScale = useRef(1);
  // Arrow entrance opacity (prototype: held at 0 until t=1.2s, then 0.8s to
  // full — landing together with the lockup at 2s). Same composing pattern:
  // applyFrame multiplies it into the arrow's scroll fast-fade.
  const arrowIn = useRef(1);
  // Scroll direction (from the triggers) + eased header opacity
  const scrollDir = useRef(1);
  const headerSmooth = useRef(1);
  const headerTargetRef = useRef(1);
  const headerElRef = useRef<Element | null>(null);
  // Seeded past the 12-frame hold in applyFrame — on a fresh load at the top
  // the nav is already in its final colors, and holding would dip the header
  // out and back in. The hold only matters returning from the scrolled state
  const topStateFrames = useRef(13);

  // The hero soundtrack ducks through a lowpass as the text scroll passes
  const applyAudio = useHeroScrubAudio(videoRef);

  useEffect(() => {
    setReduceMotionState(getReduceMotion());
  }, []);

  // Media readiness: the entrance may start once EITHER the poster image or
  // the video's first frame can display (whichever arrives first — the
  // element shows the poster until the video buffers, so both count). The
  // timeout is a safety valve: broken/unreachable media must not hold the
  // hero black forever — after it, the entrance runs as it did before this
  // gate existed.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      setMediaReady(true);
      return;
    }
    let done = false;
    const ready = () => {
      if (done) return;
      done = true;
      setMediaReady(true);
    };
    // HAVE_CURRENT_DATA: a frame is already decoded (cached/fast loads)
    if (video.readyState >= 2) {
      ready();
      return;
    }
    video.addEventListener("loadeddata", ready);
    // Raw attribute, not the .poster IDL property: an empty poster attribute
    // resolves the property to the page URL, which would probe the page HTML
    let posterProbe: HTMLImageElement | null = null;
    const posterUrl = video.getAttribute("poster");
    if (posterUrl) {
      posterProbe = new window.Image();
      posterProbe.onload = ready;
      posterProbe.src = posterUrl;
      if (posterProbe.complete && posterProbe.naturalWidth > 0) ready();
    }
    const failSafe = window.setTimeout(ready, 5000);
    return () => {
      video.removeEventListener("loadeddata", ready);
      if (posterProbe) posterProbe.onload = null;
      window.clearTimeout(failSafe);
    };
  }, []);

  // The accent markers are stripped (the per-word reveal handles accent);
  // words inside `backticks` are tagged so they keep their entered case
  // against the page's uppercase style (see .dx-verbatim). Multi-word backtick
  // phrases tag every word in the phrase.
  const words = useMemo(() => {
    const raw = (statement || "").replace(/\*\*/g, "");
    const out: { text: string; verbatim: boolean }[] = [];
    raw.split("`").forEach((segment, i) => {
      const verbatim = i % 2 === 1;
      segment
        .split(/\s+/)
        .filter(Boolean)
        .forEach((text) => out.push({ text, verbatim }));
    });
    return out;
  }, [statement]);

  const desktopIsImage = selectValue(backgroundDesktop?.backgroundType) === "image";
  const mobileIsImage = selectValue(backgroundMobile?.backgroundType) === "image";

  // ---------- Frame application (style mutation via refs, no re-renders)

  const applyFrame = () => {
    // Prototype scale model (verbatim factors): our fluid rem root re-bases
    // per breakpoint, so choreographed sizes come from these instead. The
    // prototype's mobile/tablet/desktop branches map onto the SITE's viewport
    // classes (ViewSizeContext) rather than its own 480/768 boundaries.
    const w = window.innerWidth;
    const isMobile = w <= MOBILE_MAX;
    const isTablet = !isMobile && w <= TABLET_MAX;
    const compact = isMobile || isTablet;
    const Ve = Math.min(1, w / 1440); // element scale
    const S = Math.min(1, w / 390); // mobile scale
    const fontScale = isMobile ? S : isTablet ? Math.min(1, w / 768) : Ve;
    const h = smooth.current.visual;
    const t = smooth.current.text;

    // Border via clip-path: video stays full-bleed, the frame opens around it.
    // The top band clears the site header (site paradigm, not prototype px).
    const headerPx = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 92;
    const sideMax = compact ? 16 : 40 * Ve;
    if (clipRef.current) {
      const extra = entranceClip.current;
      const side = Math.round(sideMax * (1 - h) + extra);
      const top = Math.round(headerPx * (1 - h) + extra);
      clipRef.current.style.clipPath = `inset(${top}px ${side}px ${side}px ${side}px round 1px)`;
    }
    if (overlayRef.current) {
      const base = isMobile ? 0.55 : 0.6;
      overlayRef.current.style.background = `rgba(0, 0, 0, ${base + h * 0.1})`;
    }
    // Frame dim: bright border at the top (0), dimming as the hero scrolls in —
    // the prototype fades its colorful frame from full opacity toward ~0.6 over
    // the same range, so dim ≈ h (1 - frameOpacity). Only the visible margins
    // show it; the media clip covers it as the border closes (see the stylesheet).
    if (frameDimRef.current) {
      frameDimRef.current.style.opacity = clamp01(h).toFixed(3);
    }

    // Header visibility (see Header.module.scss): scrolling UP anywhere above
    // the intro's end hides the header immediately — it only returns once the
    // border has fully formed at the top. Scrolling down it hides just while
    // the border is mid-animation (visible again from the statement zone on).
    // The top fade-in is gated on the header having actually LEFT its
    // scrolled (black) state — raw scrollY leads the React class swap by a
    // few frames, which would let black through the start of the fade — and
    // then held ~200ms more so the nav's own 0.15s color transition
    // (white -> the exhibit nav color) finishes while still hidden
    headerElRef.current ||= document.querySelector("header");
    const atTopState = headerElRef.current ? !headerElRef.current.className.includes("scrolled") : false;
    topStateFrames.current = atTopState ? topStateFrames.current + 1 : 0;
    const formed = topStateFrames.current > 12 ? clamp01(1 - h / 0.06) : 0;
    const opened = clamp01((h - 0.85) / 0.15);
    headerTargetRef.current = scrollDir.current === -1 ? formed : Math.max(formed, opened);
    headerSmooth.current += (headerTargetRef.current - headerSmooth.current) * 0.25;
    if (Math.abs(headerTargetRef.current - headerSmooth.current) < 0.01) {
      headerSmooth.current = headerTargetRef.current;
    }
    // While riding (computed below), the CSS forces the header fully visible
    // regardless of the fade value above, so the hidden attribute
    // (pointer-events: none) must not engage there — a slight scroll would
    // otherwise leave a fully visible header that can't be hovered. It only
    // gates the post-ride fade phase.
    const riding = !getReduceMotion() && t < 0.75;
    document.body.style.setProperty("--dx-header-opacity", headerSmooth.current.toFixed(3));
    document.body.toggleAttribute("data-dx-header-hidden", !riding && headerSmooth.current < 0.05);

    // Border ride (prototype): the nav is not a separate bar that fades in once
    // the border has formed — it sits ON the top border and rides up/out WITH it
    // as the border closes (and back down into view as it reopens), so the
    // border reads as the nav. The clip's top inset is headerPx*(1-h), so the
    // border's bottom edge sits at that y; translating the whole header by
    // -headerPx*h keeps the header's bottom edge locked to the border's bottom
    // edge for any h. While riding, data-dx-header-riding forces the header's
    // transparent exhibit-nav appearance (the .scrolled black state engages at
    // 10px, but the border is still on screen) and overrides hide/show-on-scroll
    // so it tracks the border instead of the scroll direction — see
    // Header.module.scss. Riding holds through the pinned phase (the header just
    // stays parked off-top once the border has closed) and ends at the pin
    // release (t >= 0.75), handing back to the --dx-header-opacity logic above
    // for the content below. Disabled under reduced motion (no choreography).
    document.body.toggleAttribute("data-dx-header-riding", riding);
    document.body.style.setProperty("--dx-header-shift", `${Math.round(-headerPx * h)}px`);

    const Ce = smoothstep(h);
    const ye = 1 - (1 - Math.min(1, t / 0.35)) ** 2;
    const Te = smoothstep(Math.min(1, t / 0.35));
    // Depart fade for the lockup/statement/prompt. Driven by RAW window.scrollY
    // (not the pinned text progress, which clamps at 1 at the trigger end) so the
    // fade eases out GRADUALLY through the scroll-away instead of being compressed
    // against the pin end. Starts at the old t=0.89 scroll point (the fade-start
    // QA approved) and runs over ~0.25*textLen of scroll — a slow exit like the
    // prototype, rather than the quick drop a pin-bounded fade forces.
    const textLen = isMobile ? TEXT_SCROLL_PX_MOBILE : TEXT_SCROLL_PX_DESKTOP;
    const exitStart = TEXT_SCROLL_START + 0.89 * textLen;
    const de = clamp01((window.scrollY - exitStart) / (textLen * 0.25));

    // The lockup scales down as the statement reveals. Its vertical position is
    // computed after all sizes are set (the centering block below) so the
    // lockup + revealed-statement composition stays centered for ANY logo
    // height — replacing the prototype's hard-coded travel offset, which assumed
    // one fixed logo height.
    const lockupScale = isMobile ? 1 - Ce * 0.2 - ye * 0.25 : 1 - Ce * 0.3 - Te * 0.35;
    // Pill→logo rest gap = the comp's 24px (pill bottom y24 → logo frame y48).
    // The baked lockup now carries the art's own headroom, so no extra clearance
    // is needed (was 36, widened for the old headroom-less bake — the pill read
    // too high above the new art). Fades out by h=0.33, so the tightening gap is
    // never visible. Hoisted — the centering below reuses it to size the pill
    // region it lifts out.
    const lockupGap = isMobile ? Math.max(0, 20 - h * 7) : Math.max(0, (24 - h * 20) * Ve);
    if (lockupRef.current) {
      lockupRef.current.style.transform = `scale(${lockupScale * entranceScale.current})`;
      lockupRef.current.style.gap = `${lockupGap}px`;
    }
    if (centerGroupRef.current) {
      centerGroupRef.current.style.opacity = String(1 - de * 0.8);
    }
    // Logo layout box. Width is the design reference (860/333 scaled). Height
    // tracks the logo's own aspect: the 252/98 reference is a "cap zone" that a
    // tall tagline-baked logo overflows by design (e.g. Rev Women 875x360), but
    // a short logo paired with a separate subhead (e.g. 1984 508x218) would
    // otherwise leave dead space below the art — decentering the lockup and
    // over-gapping the subhead. So clamp the box to the art height when it fits,
    // and keep the -10.7% overflow lift only for logos that overflow the zone.
    const lw = isMobile ? 333 * S : 860 * Ve;
    if (logoBoxRef.current) {
      const refH = isMobile ? 98 * S : 252 * Ve;
      const natW = logo?.node?.mediaDetails?.width || 860;
      const natH = logo?.node?.mediaDetails?.height || (isMobile ? 98 : 252);
      // Displayed art height: width is lw * (natW/860), height follows the ratio
      const imgH = (lw * natH) / 860;
      const overflows = imgH > refH + 0.5;
      logoBoxRef.current.style.width = `${Math.round(lw)}px`;
      logoBoxRef.current.style.height = `${Math.round(overflows ? refH : imgH)}px`;
      logoBoxRef.current.style.setProperty("--dx-logo-top", overflows ? "-10.7%" : "0px");
    }
    // Optional subheading scales with the logo (comp: 24px on the 860 box) so
    // it reads as part of the lockup; the lockup's scale transform then carries
    // it through the scroll. A 14px floor keeps it legible at mobile widths
    // (deliberate breakpoint judgement — the pure ratio dips to ~9px there).
    if (subheadRef.current) {
      subheadRef.current.style.fontSize = `${Math.max(14, Math.round((lw * 24) / 860))}px`;
    }
    // The pill renders in site rem (which re-bases per breakpoint); normalize
    // it to the prototype's scale and keep its layout height in step
    if (pillRef.current) {
      const remScale = parseFloat(getComputedStyle(document.documentElement).fontSize) / 8;
      const target = isMobile ? S : Ve;
      pillRef.current.style.transform = `scale(${target / remScale})`;
      pillRef.current.style.height = `${Math.round(25 * target)}px`;
    }
    if (arrowRef.current) {
      const ah = isMobile ? 85 * S : 171 * Ve;
      arrowRef.current.style.height = `${Math.round(ah)}px`;
      arrowRef.current.style.bottom = `${Math.round((isMobile ? 48 : 80) * (isMobile ? S : Ve))}px`;
    }

    // Pill + arrow fade out fast; the arrow also rides its own delayed
    // entrance (see the timeline) so it holds back until the lockup has begun
    // materializing. The pill rides only its parent lockup's entrance
    // (prototype: the whole lockup unit fades/unblurs as one) — an extra
    // entrance factor here would double-fade it
    const fastFade = Math.max(0, 1 - h * 3);
    if (pillRef.current) pillRef.current.style.opacity = String(fastFade);
    if (arrowRef.current) arrowRef.current.style.opacity = String(fastFade * arrowIn.current);

    // Statement: prototype sizes (64/56/32 * fontScale), line-height tightens
    // 1.375 -> 1.1. The invisible spacer mirrors size + line-height so the
    // centered group repositions with the tightening text.
    // The font also shrinks so the design-width box (below) actually fits
    // 92vw — once the box gets clamped instead, the text re-wraps to far more
    // lines than the comp and overruns the lockup and scroll prompt (worst
    // around 768-1024px, where the prototype's tablet font stays at 56)
    const nominalPx = (isMobile ? 32 : isTablet ? 56 : 64) * fontScale;
    let fontPx = Math.round(isMobile ? nominalPx : Math.min(nominalPx, (w * 0.92 * 64) / 1129));
    // 1129px is the prototype's statement box at 64px. (It was inflated to 1304
    // to compensate for the copy accidentally rendering in Söhne, which wraps
    // wider; now that the exhibit font is real Barlow the comp's 1129 box wraps
    // correctly.) Scale it with the font so line counts hold at every breakpoint
    const widthPx = Math.round(isMobile ? 358 * S : (1129 * fontPx) / 64);
    const lineHeight = isMobile ? 1.1 : 1.375 - ye * 0.275;
    // Size the statement FIRST so the measured heights below reflect the
    // current wrap + line-height.
    if (statementRef.current) {
      statementRef.current.style.fontSize = `${fontPx}px`;
      statementRef.current.style.width = `${widthPx}px`;
      statementRef.current.style.maxWidth = "1129px";
      statementRef.current.style.lineHeight = String(lineHeight);
    }

    // Dynamic vertical centering. Measure the unscaled lockup + statement
    // heights and keep the [lockup | gap | revealed-statement] composition
    // centered in the stage. The lockup scales about its CENTER, so its visual
    // center is its box center regardless of scale — which is why the scale
    // term cancels out of the lockup's offset: the box simply centers and rides
    // up by half the statement block as it enters. Works for any logo height
    // (no hard-coded offset). centerGroup is inset:0, so its height is the
    // stage height; the lockup is top-anchored in it (justify-content:flex-start).
    const stageH = centerGroupRef.current?.offsetHeight || window.innerHeight;
    const lockupNatH = lockupRef.current?.offsetHeight || 0;
    let stmtNatH = statementRef.current?.offsetHeight || 0;
    const pillH = pillRef.current?.offsetHeight || 0;

    if (isMobile && stmtNatH > 0 && statementRef.current && promptRef.current) {
      const revealedScale = 0.55; // lockupScale at full reveal: 1 - 0.2 - 0.25
      const lockupGapR = 13; // lockupGap at the revealed (h≈1) state: 20 - 7
      const pillRegionR = (pillH > 0 ? pillH + lockupGapR : 0) * revealedScale;
      // Drip clearance scales with the lockup transform, so normalize the live
      // measurement to the revealed scale.
      const lockupBottom = lockupRef.current?.getBoundingClientRect().bottom ?? 0;
      const artBottom = logoBoxRef.current?.querySelector("img")?.getBoundingClientRect().bottom ?? lockupBottom;
      const dripClearR = lockupScale > 0 ? (Math.max(0, artBottom - lockupBottom) / lockupScale) * revealedScale : 0;
      const slotGapR = 24;
      // Stage-local top of the prompt (pinned via bottom in the stylesheet).
      const promptBottom = parseFloat(getComputedStyle(promptRef.current).bottom) || 0;
      const promptTopLocal = stageH - promptBottom - promptRef.current.offsetHeight;
      // Revealed statement bottom = constTop + stmtHeight/2 (the centering's
      // -height/2 and the +height below the lockup net to +height/2).
      const constTop =
        stageH / 2 + slotGapR / 2 - pillRegionR / 2 + (lockupNatH * revealedScale) / 2 + dripClearR;
      const safeGap = 16;
      const room = promptTopLocal - safeGap - constTop; // headroom for stmtHeight/2
      // Box width is fixed (stays ~92% of the screen), so shrinking the font
      // reflows to fewer lines — height falls ~with font², not linearly — hence
      // the sqrt. It's an estimate; re-measure below so the centering is exact.
      const fit = Math.min(1, Math.max(0.7, Math.sqrt(Math.max(0, (2 * room) / stmtNatH))));
      if (fit < 1) {
        fontPx = Math.round(fontPx * fit);
        statementRef.current.style.fontSize = `${fontPx}px`;
        stmtNatH = statementRef.current.offsetHeight;
      }
    }

    // Desktop logo→copy gap tightened to match the prototype (was 48 * Ve)
    const slotGap = isMobile ? 24 : 24 * Ve;
    const hasStmt = words.length > 0 && stmtNatH > 0;
    const reveal = hasStmt ? ye : 0;
    const block = (hasStmt ? slotGap : 0) + stmtNatH; // gap + statement text
    // Center on the LOGO + SUBHEAD, ignoring the EXHIBIT pill above them (per
    // design). Lift the pill + its gap out of the centering (scaled with the
    // lockup) so the logo/subhead is what sits centered — the pill still
    // renders above; the lockup just rides a touch higher. pillH is 0 in
    // reduced motion (pill hidden), so the lift correctly vanishes there.
    const pillRegion = pillH > 0 ? pillH + lockupGap : 0;
    // Lift the fully-revealed composition to match the prototype's (higher)
    // vertical position: ~17px target lift, plus half the gap we tightened above
    // (shrinking slotGap re-centers the logo downward, so add it back). Ramped by
    // reveal so the pre-reveal logo stays at its natural center.
    const composeLift = isMobile ? 0 : reveal * (27 + 12 * Ve);
    const boxCenter = stageH / 2 - (reveal * block) / 2 - (pillRegion * lockupScale) / 2 - composeLift;
    if (centerGroupRef.current) {
      centerGroupRef.current.style.transform = `translateY(${Math.round(boxCenter - lockupNatH / 2)}px)`;
    }
    if (statementRef.current) {
      // Statement sits just below the lockup as it reveals. The lockup's layout
      // box clips the logo art's tails (the image overflows it — see
      // --dx-logo-top), so the copy was colliding with the drips on the way in.
      // Measure how far the RENDERED art extends below the lockup box and clear
      // that too, so the copy clears the actual tails (matching the prototype).
      const lockupBottom = lockupRef.current?.getBoundingClientRect().bottom ?? 0;
      const artBottom = logoBoxRef.current?.querySelector("img")?.getBoundingClientRect().bottom ?? lockupBottom;
      const dripClear = Math.max(0, artBottom - lockupBottom);
      const top = boxCenter + (lockupNatH * lockupScale) / 2 + dripClear + reveal * slotGap;
      const lift = (1 - ye) * (isMobile ? 16 : 8);
      statementRef.current.style.top = `${Math.round(top)}px`;
      statementRef.current.style.transform = `translate(-50%, ${Math.round(lift)}px)`;
      statementRef.current.style.opacity = String(ye * (1 - de * 0.8));
      // Word reveal — desktop/tablet only. On mobile the statement renders plain
      // white (the prototype's mobileSimpleText), so there's nothing to toggle.
      if (!isMobile) {
        const visibleWords = Math.floor(
          clamp01((t - WORD_REVEAL_START) / (WORD_REVEAL_END - WORD_REVEAL_START)) * words.length
        );
        if (visibleWords !== revealedCount.current) {
          revealedCount.current = visibleWords;
          const spans = statementRef.current.querySelectorAll("span");
          spans.forEach((span, i) => span.toggleAttribute("data-revealed", i < visibleWords));
        }
      }
    }

    if (promptRef.current) {
      promptRef.current.style.opacity = String(ye * (1 - de * 0.8));
    }

    applyAudio(t);
  };

  // Adaptive lerp loop (prototype smoothing)
  const tick = () => {
    const reduce = getReduceMotion();
    const dVisual = raw.current.visual - smooth.current.visual;
    const dText = raw.current.text - smooth.current.text;
    for (const [key, delta] of [["visual", dVisual], ["text", dText]] as const) {
      if (Math.abs(delta) > 0.4) {
        smooth.current[key] = raw.current[key];
      } else {
        // Mobile uses the prototype's stiffer lerp (Le)
        const base = reduce || window.innerWidth <= MOBILE_MAX ? 0.5 : 0.3 - smooth.current[key] * 0.18;
        const boost = Math.min(1, Math.abs(delta) * 3);
        smooth.current[key] += delta * (base + (1 - base) * boost);
      }
    }
    applyFrame();
    // The header opacity eases independently — keep ticking until it settles,
    // including through the post-arrival hold that counts in applyFrame
    const headerSettling =
      headerSmooth.current !== headerTargetRef.current ||
      (topStateFrames.current > 0 && topStateFrames.current <= 12);
    if (Math.abs(dVisual) > 1e-4 || Math.abs(dText) > 1e-4 || headerSettling) {
      rafId.current = requestAnimationFrame(tick);
    } else {
      smooth.current.visual = raw.current.visual;
      smooth.current.text = raw.current.text;
      rafId.current = null;
    }
  };

  const schedule = () => {
    rafId.current ||= requestAnimationFrame(tick);
  };

  useGSAP(
    () => {
      if (!wrapperRef.current) return;
      if (reduceMotion) {
        // No scroll choreography. Paint the settled end-state once (text just
        // shy of the scroll-away fade) so the lockup + revealed statement land
        // centered via the SAME dynamic logic as the animated path — no
        // separate hand-tuned static positioning to drift out of sync.
        smooth.current = { visual: 1, text: 0.7 };
        raw.current = { visual: 1, text: 0.7 };
        // A killed mid-entrance timeline (reduceMotion flip) may leave the
        // entrance scale/arrow partway — the static paint needs them settled
        entranceScale.current = 1;
        arrowIn.current = 1;
        applyFrame();
        const onResize = () => applyFrame();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
      }
      // Hold: the __cover keeps the stage black until the poster or video can
      // display; the whole entrance (border, media, lockup, arrow) builds on
      // the mediaReady rerun so nothing animates over an empty media box
      if (!mediaReady) return;
      const isMobile = window.innerWidth <= MOBILE_MAX;
      const textLength = isMobile ? TEXT_SCROLL_PX_MOBILE : TEXT_SCROLL_PX_DESKTOP;

      ScrollTrigger.create({
        trigger: wrapperRef.current,
        start: "top top",
        end: `+=${VISUAL_SCROLL_PX}`,
        scrub: true,
        onUpdate: (self) => {
          raw.current.visual = self.progress;
          scrollDir.current = self.direction;
          schedule();
        },
      });
      ScrollTrigger.create({
        trigger: wrapperRef.current,
        start: `top+=${TEXT_SCROLL_START} top`,
        end: `+=${textLength}`,
        onUpdate: (self) => {
          raw.current.text = self.progress;
          scrollDir.current = self.direction;
          schedule();
        },
      });

      // Entrance: the border clip reveals outward and the video settles with a
      // ~1.2s ease-IN-OUT (power3.inOut) — the prototype opens gently (slow
      // start), accelerates through the middle, then eases to rest. The earlier
      // 0.9s power2.out front-loaded the motion and finished too soon, reading
      // as snappy rather than elegant (measured against the prototype's reveal).
      // The lockup runs its own 2s entrance below.
      const clipProxy = { v: 100 };
      entranceClip.current = 100;
      const tl = gsap.timeline();
      // The cover held the stage black while the media loaded — dissolve it
      // with the border reveal so the background art and dimmed media emerge
      // from black as one gesture (autoAlpha parks it at visibility:hidden)
      if (coverRef.current) tl.to(coverRef.current, { autoAlpha: 0, duration: 1.4, ease: "power3.inOut" }, 0);
      tl.to(clipProxy, {
        v: 0,
        duration: 1.4,
        ease: "power3.inOut",
        onUpdate: () => {
          entranceClip.current = clipProxy.v;
          applyFrame();
        },
      }, 0);
      if (videoRef.current) tl.fromTo(videoRef.current, { scale: 1.1 }, { scale: 1, duration: 1.4, ease: "power3.inOut" }, 0);
      // Lockup entrance (prototype, verbatim): the whole lockup unit — pill,
      // logo, and optional subhead — fades in while unblurring from 12px and
      // scaling up from 0.88, everything 2s expo.inOut from t=0. Opacity and
      // blur tween on the element directly (nothing else writes those two);
      // scale goes through entranceScale + applyFrame so it composes with the
      // scroll-driven lockup scale. Blur clears on complete — a lingering
      // blur(0px) would keep a filter compositing layer alive for no reason.
      if (lockupRef.current) {
        gsap.set(lockupRef.current, { opacity: 0, filter: "blur(12px)" });
        tl.to(lockupRef.current, { opacity: 1, filter: "blur(0px)", duration: 2, ease: "expo.inOut", clearProps: "filter" }, 0);
        const scaleProxy = { v: 0.88 };
        entranceScale.current = scaleProxy.v;
        tl.to(scaleProxy, {
          v: 1,
          duration: 2,
          ease: "expo.inOut",
          onUpdate: () => {
            entranceScale.current = scaleProxy.v;
            applyFrame();
          },
        }, 0);
      }
      // Arrow entrance (prototype, verbatim): held back until 1.2s, then 0.8s
      // power2.out to full — landing with the lockup at 2s. Riding the border
      // reveal (done at 1.4s) surfaced it well before the lockup had formed
      const arrowProxy = { v: 0 };
      arrowIn.current = 0;
      tl.to(arrowProxy, {
        v: 1,
        duration: 0.8,
        ease: "power2.out",
        onUpdate: () => {
          arrowIn.current = arrowProxy.v;
          applyFrame();
        },
      }, 1.2);
      applyFrame();

      // Fluid rem handles resize for layout; recompute frame values on resize
      const onResize = () => schedule();
      window.addEventListener("resize", onResize);
      // The hero wrapper starts a few px down the page, so the triggers go
      // quiet before scroll reaches 0 — keep frames running over the final
      // pixels so the header's top fade-in gate (scrollY < 5) re-evaluates
      window.addEventListener("scroll", onResize, { passive: true });
      return () => {
        window.removeEventListener("resize", onResize);
        window.removeEventListener("scroll", onResize);
      };
    },
    // revertOnUpdate: the reduceMotion flip must kill the scroll triggers and
    // entrance timeline, not leave them driving the static layout
    { dependencies: [reduceMotion, mediaReady], revertOnUpdate: true, scope: wrapperRef }
  );

  useEffect(() => () => {
    if (rafId.current) cancelAnimationFrame(rafId.current);
    document.body.style.removeProperty("--dx-header-opacity");
    document.body.removeAttribute("data-dx-header-hidden");
    document.body.style.removeProperty("--dx-header-shift");
    document.body.removeAttribute("data-dx-header-riding");
  }, []);

  if (!videoUrl) return null;

  const videoSrc = (typeof window !== "undefined" && window.innerWidth <= MOBILE_MAX && videoUrlMobile) || videoUrl;

  const renderBackground = (
    data: NonNullable<HeroData>["backgroundDesktop"] | NonNullable<HeroData>["backgroundMobile"],
    isImage: boolean,
    variant: string
  ) => (
    <div
      className={cx("dx-heroin__background", `dx-heroin__background--${variant}`)}
      style={!isImage ? { background: accentVar(data?.accent) } : undefined}
    >
      {isImage && <Image image={data?.image} className={cx("dx-heroin__background-image")} />}
    </div>
  );

  const statementBlock = () => (
    <p
      ref={statementRef}
      className={cx("dx-heroin__statement", "dx-display-1")}
      aria-label={statement}
      // Pre-reveal word color (spans use var(--highlight-color)); empty falls
      // back to the page's first accent set on the wrapper by the template
      style={{ "--highlight-color": accentVar(statementAccent) } as React.CSSProperties}
    >
      {words.map((word, i) => (
        <span
          key={i}
          aria-hidden="true"
          data-revealed={reduceMotion ? true : undefined}
          className={word.verbatim ? "dx-verbatim" : undefined}
        >
          {word.text}{" "}
        </span>
      ))}
    </p>
  );

  return (
    <section
      ref={wrapperRef}
      className={cx("dx-heroin", { "dx-heroin--static": reduceMotion })}
      style={
        !reduceMotion
          // Pin lengths feed the stylesheet (responsive via media query there);
          // max(100svh, 650px) in the SCSS keeps the same release point at
          // short viewports (650px stage floor)
          ? ({
              "--dx-pin-desktop": `${PIN_SCROLL_DESKTOP}px`,
              "--dx-pin-mobile": `${PIN_SCROLL_MOBILE}px`,
            } as React.CSSProperties)
          : undefined
      }
    >
      <div className={cx("dx-heroin__stage")}>
        {renderBackground(backgroundDesktop, desktopIsImage, "desktop")}
        {renderBackground(backgroundMobile, mobileIsImage, "mobile")}

        {/* Dims the colorful frame as the hero scrolls in (opacity driven per
            frame); bright at the top, so no dim on load */}
        <div ref={frameDimRef} className={cx("dx-heroin__frame-dim")} />

        {/* Full-bleed video clipped to the frame; the clip opens with scroll */}
        <div ref={clipRef} className={cx("dx-heroin__media")}>
          {/* Autoplays muted; unmutes only after the user opts into sound */}
          <video
            ref={videoRef}
            className={cx("dx-heroin__video")}
            src={videoSrc || ""}
            poster={videoPoster?.node?.mediaItemUrl || ""}
            crossOrigin="anonymous"
            autoPlay
            muted
            loop
            playsInline
          />
          <div ref={overlayRef} className={cx("dx-heroin__media-overlay")} />
        </div>

        {/* Center group: lockup above an invisible statement-sized slot, the
            pair centered together exactly like the prototype */}
        <div ref={centerGroupRef} className={cx("dx-heroin__center")}>
          <div ref={lockupRef} className={cx("dx-heroin__lockup")}>
            {/* Pill fill follows the chosen page accent (TypePill's background is
                var(--highlight-color)); empty = inherits the page default */}
            <div
              ref={pillRef}
              className={cx("dx-heroin__pill")}
              style={{ "--highlight-color": accentVar(pillAccent) } as React.CSSProperties}
            >
              <TypePill type="exhibit" copy={labels?.postTypeLabels?.exhibit} className={cx("dx-heroin__pill-type")} />
            </div>
            {logo && (
              <div
                ref={logoBoxRef}
                className={cx("dx-heroin__logo")}
                // Per-logo width ratio (natural width over the 860px reference
                // box) so each logo renders at its designed proportion — see
                // the --dx-logo-w-pct note in the stylesheet
                style={
                  logo.node?.mediaDetails?.width
                    ? ({ "--dx-logo-w-pct": logo.node.mediaDetails.width / 860 } as React.CSSProperties)
                    : undefined
                }
              >
                <Image image={logo} alt={logoAltText || ""} className={cx("dx-heroin__logo-image")} />
              </div>
            )}
            {/* Optional CMS subheading — sits below the logo, scaling with it
                (font size set per frame). Empty = today's behavior (tagline
                baked into the logo art). */}
            {subheading && (
              <p
                ref={subheadRef}
                className={cx("dx-heroin__subhead", "dx-subhead")}
                dangerouslySetInnerHTML={{ __html: getExhibitMarkup(subheading) }}
              />
            )}
          </div>
        </div>

        {/* Statement — words reveal accent -> white as the user scrolls.
            Position (top/transform) is computed per frame so it sits just below
            the centered lockup as it reveals. */}
        {words.length > 0 && statementBlock()}

        {/* Down arrow — default icon (tinted by the chosen accent) or editor
            image override (color baked into the image) */}
        <div
          ref={arrowRef}
          className={cx("dx-heroin__arrow")}
          style={{ "--dx-arrow-color": accentVar(arrowAccent) } as React.CSSProperties}
        >
          {arrowOverride ? (
            <Image image={arrowOverride} className={cx("dx-heroin__arrow-image")} />
          ) : (
            <ArrowDownThin className={cx("dx-heroin__arrow-icon")} />
          )}
        </div>

        <div ref={promptRef} className={cx("dx-heroin__prompt")}>
          <span className={cx("dx-caption")}>{labels?.continueScrolling || "Continue Scrolling"}</span>
          <ArrowDownCta className={cx("dx-heroin__prompt-arrow")} />
        </div>

        {/* Holds the stage black until the poster or video can display —
            without it a slow connection reveals the border art around an
            empty media box and the poster blinks in mid-entrance. The
            entrance timeline dissolves it. */}
        <div ref={coverRef} className={cx("dx-heroin__cover")} />
      </div>
    </section>
  );
}
